import React, { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import Swal from "sweetalert2";
import { FaEye } from "react-icons/fa";
import { IoClose } from "react-icons/io5";

const API = process.env.REACT_APP_API_BASE_URL;

const WdAdmin = () => {
  const token = Cookies.get("token");

  const [withdraws, setWithdraws] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [balance, setBalance] = useState(0);
  const [totalCommission, setTotalCommission] = useState(0);
  const [totalWithdraw, setTotalWithdraw] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const [selectedWithdraw, setSelectedWithdraw] = useState<any>(null);
  const [showDetail, setShowDetail] = useState(false);

  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const formatPrice = (price: any) => {
    return new Intl.NumberFormat("id-ID").format(price);
  };

  /*
  ============================
  ADMIN BALANCE
  ============================
  */

  const fetchBalance = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/affiliate/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setBalance(res.data.balance);
      setTotalCommission(res.data.total_commission);
      setTotalWithdraw(res.data.total_withdraw);
    } catch (err) {
      console.log(err);
    }
  };

  /*
  ============================
  GET WITHDRAW LIST
  ============================
  */

  const fetchWithdraws = async (page = 1) => {
    setLoading(true);

    try {
      const res = await axios.get(`${API}/api/admin/withdrawals?page=${page}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setWithdraws(res.data.data);
      setCurrentPage(res.data.current_page);
      setLastPage(res.data.last_page);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  /*
  ============================
  APPROVE
  ============================
  */

  const approveWithdraw = async (id: number) => {
    const confirm = await Swal.fire({
      title: "Approve Withdraw?",
      text: "Yakin ingin menyetujui withdraw ini?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya Approve",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await axios.post(
        `${API}/api/admin/withdraw/${id}/approve`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      Swal.fire("Berhasil", res.data.message, "success");

      setShowDetail(false);
      fetchWithdraws(currentPage);
      fetchBalance();
    } catch (err: any) {
      Swal.fire("Error", err.response?.data?.message || "Gagal approve", "error");
    }
  };

  /*
  ============================
  REJECT
  ============================
  */

  const rejectWithdraw = async () => {
    const confirm = await Swal.fire({
      title: "Reject Withdraw?",
      text: "Yakin ingin menolak withdraw ini?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya Reject",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await axios.post(
        `${API}/api/admin/withdraw/${selectedWithdraw.id}/reject`,
        { reason: rejectReason },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      Swal.fire("Berhasil", res.data.message, "success");

      setShowDetail(false);
      setRejectMode(false);
      setRejectReason("");

      fetchWithdraws(currentPage);
      fetchBalance();
    } catch (err: any) {
      Swal.fire("Error", err.response?.data?.message || "Gagal reject", "error");
    }
  };

  useEffect(() => {
    fetchBalance();
    fetchWithdraws();
  }, []);

  return (
    <div className="mt-7">
      {/* SUMMARY */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border rounded-xl p-5 shadow">
          <p className="text-sm text-gray-500">Saldo Affiliate</p>
          <h2 className="text-2xl font-bold text-green-600">Rp {formatPrice(balance)}</h2>
        </div>

        <div className="bg-white border rounded-xl p-5 shadow">
          <p className="text-sm text-gray-500">Total Komisi</p>
          <h2 className="text-2xl font-bold">Rp {formatPrice(totalCommission)}</h2>
        </div>

        <div className="bg-white border rounded-xl p-5 shadow">
          <p className="text-sm text-gray-500">Total Withdraw</p>
          <h2 className="text-2xl font-bold text-red-500">Rp {formatPrice(totalWithdraw)}</h2>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-xl shadow border w-full">
        <div className="overflow-x-auto">
          <table className="min-w-[700px] w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-3 text-left">Affiliate</th>
                <th className="p-3 text-left">Bank</th>
                <th className="p-3 text-left">Rekening</th>
                <th className="p-3 text-left">Nominal</th>
                <th className="p-3 text-left">Tanggal</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Aksi</th>
              </tr>
            </thead>

            <tbody>
              {withdraws.map((withdraw) => (
                <tr key={withdraw.id} className="border-t">
                  <td className="p-3">{withdraw.affiliate.name}</td>
                  <td className="p-3">{withdraw.bank_name}</td>
                  <td className="p-3">{withdraw.account_number}</td>
                  <td className="p-3 font-semibold">Rp {formatPrice(withdraw.amount)}</td>
                  <td className="p-3">{new Date(withdraw.created_at).toLocaleString("id-ID")}</td>

                  <td className="p-3">
                    {withdraw.status === "approved" && <span className="bg-green-100 text-green-600 px-2 py-1 rounded text-xs">Approved</span>}
                    {withdraw.status === "pending" && <span className="bg-yellow-100 text-yellow-600 px-2 py-1 rounded text-xs">Pending</span>}
                    {withdraw.status === "rejected" && <span className="bg-red-100 text-red-600 px-2 py-1 rounded text-xs">Rejected</span>}
                  </td>

                  <td className="p-3">
                    <FaEye
                      className="cursor-pointer text-blue-500"
                      onClick={() => {
                        setSelectedWithdraw(withdraw);
                        setShowDetail(true);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINATION */}
      <div className="flex justify-end gap-3 mt-5">
        <button disabled={currentPage === 1} onClick={() => fetchWithdraws(currentPage - 1)} className="px-4 py-2 bg-gray-200 rounded">
          Prev
        </button>

        <span>
          {currentPage} / {lastPage}
        </span>

        <button disabled={currentPage === lastPage} onClick={() => fetchWithdraws(currentPage + 1)} className="px-4 py-2 bg-gray-200 rounded">
          Next
        </button>
      </div>

      {/* MODAL */}
      {showDetail && selectedWithdraw && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-[420px] relative">
            {/* CLOSE */}
            <div className="absolute right-4 top-4 cursor-pointer">
              <IoClose size={22} onClick={() => setShowDetail(false)} />
            </div>

            <div className="p-6 space-y-3 text-sm">
              <p>
                <b>Affiliate:</b> {selectedWithdraw.affiliate.name}
              </p>
              <p>
                <b>Bank:</b> {selectedWithdraw.bank_name}
              </p>
              <p>
                <b>Rekening:</b> {selectedWithdraw.account_number}
              </p>
              <p>
                <b>Nama:</b> {selectedWithdraw.account_name}
              </p>
              <p>
                <b>Nominal:</b> Rp {formatPrice(selectedWithdraw.amount)}
              </p>
              <p>
                <b>Status:</b> {selectedWithdraw.status}
              </p>

              {selectedWithdraw.reject_reason && (
                <p className="text-red-500">
                  <b>Alasan Reject:</b> {selectedWithdraw.reject_reason}
                </p>
              )}

              {/* REJECT FORM */}
              {rejectMode && (
                <>
                  <textarea placeholder="Alasan penolakan..." className="w-full border rounded-lg p-2 mt-3" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />

                  <button onClick={rejectWithdraw} className="w-full bg-red-500 text-white py-2 rounded-lg mt-2">
                    Simpan Penolakan
                  </button>
                </>
              )}

              {!rejectMode && selectedWithdraw.status === "pending" && (
                <div className="flex gap-3 pt-4">
                  <button onClick={() => approveWithdraw(selectedWithdraw.id)} className="flex-1 bg-green-500 text-white py-2 rounded-lg">
                    Approve
                  </button>

                  <button onClick={() => setRejectMode(true)} className="flex-1 bg-red-500 text-white py-2 rounded-lg">
                    Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WdAdmin;
